import { useEffect, useState } from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, Theme } from "@mui/material/styles";
import { getIsoLocaleFromCountry, Locales } from "@simplyk/common";
import { useRouter } from "next/router";

import { LocaleProvider } from "../../../../common/contexts/LocaleContext";
import { useDisableSafariBackForwardCache } from "../../../../common/hooks/useDisableSafariBackForwardCache";
import { MinimalLayout } from "../../../../common/layouts/MinimalLayout";
import { AmplitudeProvider } from "../../../../common/providers/AmplitudeProvider";
import { PageLoadingProvider } from "../../../../common/providers/PageLoadingProvider";
import { zeffyTheme } from "../../../../common/theme/theme";

import { useLogParamsEvent } from "@/common/hooks/amplitude/useLogParamsEvent";
import { OrganizationPageOutput } from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { NotificationBannerHeightProvider } from "@/components/NotificationBanners/NotificationBannerHeightContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { PublicRoute } from "@/components/Routes/PublicRoute";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { ZeffySnackbarProvider } from "@/dashboard-design-system/Snackbar";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DashboardZeffyLoader } from "@/dashboard-design-system/ZeffyLoader/DashboardZeffyLoader";

import "react-phone-input-2/lib/material.css";
import "react-phone-input-2/lib/style.css";

export const AppOrganizationPageProvider = ({
  children,
  locale,
  theme,
  organization,
  fields,
  SSREnabled = true,
}: {
  children: React.ReactNode;
  locale: Locales;
  theme: Theme;
  organization: OrganizationPageOutput["organization"];
  fields: { locale: Locales }[];
  SSREnabled?: boolean;
}) => {
  useLogParamsEvent();
  useDisableSafariBackForwardCache();
  const router = useRouter();
  const forcedLocale = fields?.length === 1 ? fields[0].locale : undefined;
  const forcedIsoLocale = forcedLocale ? getIsoLocaleFromCountry(organization?.country, forcedLocale) : undefined;

  // Disable SSR as it creates memory leaks
  const [key, setKey] = useState(0);
  useEffect(() => {
    if (!SSREnabled) {
      setKey(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (key === 0 && !SSREnabled) {
    return <DashboardZeffyLoader />;
  }

  return (
    <>
      <PageLoadingProvider isLoading={router.isFallback} disableLoading>
        <ThemeProvider theme={theme || zeffyTheme}>
          <NotificationBannerHeightProvider>
            <CssBaseline />
            <AmplitudeProvider>
              <LocaleProvider forcedIsoLocale={forcedIsoLocale} formDefaultLocale={locale}>
                <ZeffySnackbarProvider>
                  <PublicRoute>
                    <MinimalLayout hideBackground>{children}</MinimalLayout>
                  </PublicRoute>
                </ZeffySnackbarProvider>
              </LocaleProvider>
            </AmplitudeProvider>
          </NotificationBannerHeightProvider>
        </ThemeProvider>
      </PageLoadingProvider>
    </>
  );
};
